import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
} from '@mui/material';
import {
  YouTube as TwitchIcon,
  CalendarToday as CalendarIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';

const QPUSProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/Twitch/QPUS.png"
          alt="Logo Questions Pour Un Streamer"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Questions Pour Un Streamer
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Zevent 2021/2022/2024
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Émission interactive développée pour le Zevent avec le streamer Etoiles, 
        inspirée du jeu "Questions pour un Champion". 
        Le système reproduit toutes les manches emblématiques et mécaniques 
        originales avec une régie technique complète incluant buzzers, pupitres 
        de contrôle et gestion automatisée des scènes VMix pour les face-à-face.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Fonctionnalités principales */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Fonctionnalités principales
        </Typography>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Reproduction fidèle de Questions pour un Champion
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Toutes les manches originales
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Système de buzzers physiques
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Gestion automatique des scènes VMix
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Pupitres avec écrans de contrôle
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Affichage scores temps réel
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Habillage graphique Unity
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Scènes face-à-face automatisées
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Technologies */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Technologies utilisées
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['C#', 'Unity', 'Socket.IO', 'API VMix', '.NET', 'Hardware Integration', 'Real-time Graphics'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold' 
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Fidélité au format original :</strong> Reproduction exacte des mécaniques 
          de "Questions pour un Champion" avec adaptation parfaite des règles, timings 
          et transitions.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Orchestration VMix complexe :</strong> Automatisation sophistiquée des 
          changements de scènes VMix pour les face-à-face.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Gestion multi-manches :</strong> Architecture modulaire capable de gérer 
          les différentes manches du jeu (4 à la suite, face-à-face, course-poursuite) 
          avec leurs règles spécifiques et transitions fluides.
        </Typography>
        <Typography variant="body2">
          <strong>Synchronisation temps critique :</strong> Coordination millisecondes entre 
          buzzers, affichage scores, habillage Unity et diffusion VMix.
        </Typography>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Liens actions */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<TwitchIcon />}
          href="https://www.youtube.com/watch?v=b-xYLjpxxJs&t=13283s"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #8b5cf6 0%, #0f766e 100%)',
            },
          }}
        >
          Voir l'émission
        </Button>
      </Box>
    </Box>
  );
};

export default QPUSProject;




